import React, { useState, useEffect } from 'react';
import { Link, Copy, RefreshCw, Users } from 'lucide-react';

const ClientRegistrationLink = () => {
  const [registrationUrl, setRegistrationUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchRegistrationLink();
  }, []);

  const fetchRegistrationLink = async () => {
    try {
      const response = await fetch('/api/client-registration/my-link', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setRegistrationUrl(data.data.registrationUrl);
      } else {
        console.error('Failed to fetch registration link:', data.message);
      }
    } catch (error) {
      console.error('Error fetching registration link:', error);
    } finally {
      setLoading(false);
    }
  };

  const regenerateLink = async () => {
    setRegenerating(true);
    try {
      const response = await fetch('/api/client-registration/regenerate-my-link', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setRegistrationUrl(data.data.registrationUrl);
        alert('Registration link regenerated successfully!');
      } else {
        alert('Failed to regenerate registration link');
      }
    } catch (error) {
      console.error('Error regenerating registration link:', error);
      alert('Failed to regenerate registration link. Please try again.');
    } finally {
      setRegenerating(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(registrationUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = registrationUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-10 bg-gray-200 rounded mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Users className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Client Registration Link</h3>
        </div>
        <button
          onClick={regenerateLink}
          disabled={regenerating}
          className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          title="Generate new link (invalidates current link)"
        >
          <RefreshCw className={`w-4 h-4 ${regenerating ? 'animate-spin' : ''}`} />
          <span>{regenerating ? 'Regenerating...' : 'Regenerate'}</span>
        </button>
      </div>

      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Share this link with potential clients so they can register their information directly with you.
        </p>

        <div className="flex items-center space-x-2">
          <div className="flex-1 relative">
            <input
              type="text"
              value={registrationUrl}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
            />
          </div>
          <button
            onClick={copyToClipboard}
            className={`flex items-center space-x-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              copied
                ? 'bg-green-100 text-green-700 border border-green-300'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <Copy className="w-4 h-4" />
            <span>{copied ? 'Copied!' : 'Copy'}</span>
          </button>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex items-start space-x-2">
            <Link className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-blue-900 mb-1">How to use this link:</p>
              <ul className="text-blue-800 space-y-1 list-disc list-inside">
                <li>Share this link via email, text, or social media</li>
                <li>Clients can fill out their travel information directly</li>
                <li>You'll receive an email notification when someone registers</li>
                <li>Client information will appear in your Clients section</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
          <p className="text-sm text-yellow-800">
            <strong>Security Note:</strong> If you suspect your link has been compromised, use the "Regenerate" button to create a new link. The old link will stop working immediately.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClientRegistrationLink;
